/**
 * Admin → Model Matrix tab — merged AI Providers + Prompts & Templates
 * (docs/superpowers/plans/2026-08-30-model-per-task-matrix.md, Task 15).
 *
 * Real implementation of the screen approved (with mock data) in Task 14's
 * prototype (`ModelMatrixPrototypeTab.tsx`) — same layout/behaviour, wired to
 * the real API hooks (`useAiProviders`/`useUpdateAiProviders`/`usePrompts`/
 * `useUpdatePrompt`) instead of static fixtures. Replaces `AiProvidersTab.tsx`
 * + `PromptsTab.tsx` as this Settings tab's content.
 *
 * Layout (human-approved, Task 14 review):
 *   - Top: two provider cards (Primary / Secondary) — Provider dropdown + API
 *     key field + a source badge ('db' → "saved", 'env' → "from .env"). The
 *     model dropdown that used to live here is REMOVED — model selection now
 *     lives entirely in the matrix below. Its OWN "Save providers" button
 *     PUTs both slots (E05-F07 `/admin/ai-providers`).
 *   - Below: one row per prompt (`usePrompts()`), grouped by `prompt_key`
 *     prefix (Pre-Kickoff / Kick-off / Documents & Emails / Salary / Research
 *     / Other catch-all). Columns: Task, Primary model, Fallback model,
 *     Action ("View/Edit"). Its OWN "Save" button PATCHes only the rows whose
 *     model picks changed (one PATCH per prompt, matching `useUpdatePrompt`'s
 *     `/admin/prompts/:promptKey` contract) — not a single merged Save button
 *     (explicit human decision: different payloads/endpoints).
 *   - "View/Edit" opens the existing prompt editor fields (title, max_tokens,
 *     active, description, variables, template, output_format) WITHOUT the
 *     `model` field — that now lives in the row's own two columns.
 *
 * SECURITY: same key-masking invariants as the former AiProvidersTab — the
 * password field is never prefilled with the real key; re-saving without
 * typing a key omits `apiKey` (keep).
 */
import { useEffect, useMemo, useState } from 'react';
import {
  AI_PROVIDERS,
  AI_PROVIDER_MODELS,
  CUSTOM_MODEL_OPTION,
  INVALID_VARIABLES_JSON_MESSAGE,
  parseInputVariables,
  type AdminPromptDTO,
  type AdminPromptUpdateBody,
  type AiProvider,
  type AiProviderSlot,
} from '@eer/shared';
import { Dialog, DialogFooter, useToast } from '../ui/index.js';
import { errorMessage } from '../api/client.js';
import {
  useAiProviders,
  useUpdateAiProviders,
  useAiProviderModels,
  useRefreshAiProviderModels,
  slotUpdateFromForm,
  type ApiKeyAction,
} from './aiProvidersApi.js';
import { usePrompts, useUpdatePrompt } from './promptsApi.js';
import { PromptTestDialog } from './PromptTestDialog.js';

const FIELD_CLASS =
  'w-full rounded border border-border px-3 py-2 text-sm focus:border-primary focus:outline-none';

const PROVIDER_LABEL: Record<AiProvider, string> = {
  anthropic: 'Anthropic',
  openai: 'OpenAI',
};

// ---------------------------------------------------------------------------
// Grouping — verbatim mapping from the spec / plan Task 15 snippet.
// ---------------------------------------------------------------------------

const GROUP_ORDER = ['pre_kickoff', 'kickoff', 'documents', 'salary', 'research', 'other'] as const;
type GroupId = (typeof GROUP_ORDER)[number];

const GROUP_LABEL: Record<GroupId, string> = {
  pre_kickoff: 'Pre-Kickoff',
  kickoff: 'Kick-off',
  documents: 'Documents & Emails',
  salary: 'Salary',
  research: 'Research',
  other: 'Other',
};

/** Testid slug — dash-cased (`pre_kickoff` -> `pre-kickoff`). */
function groupSlug(group: GroupId): string {
  return group.replace(/_/g, '-');
}

function groupForPromptKey(promptKey: string): GroupId {
  if (promptKey.startsWith('pre_kickoff_')) return 'pre_kickoff';
  if (promptKey.startsWith('kickoff_')) return 'kickoff';
  if (promptKey.startsWith('generate_') || promptKey.startsWith('regenerate_')) return 'documents';
  if (promptKey.startsWith('salary_')) return 'salary';
  if (promptKey.startsWith('research_')) return 'research';
  return 'other';
}

// ---------------------------------------------------------------------------
// Provider cards (top) — trimmed SlotEditor: Provider + API key only, no Model.
// ---------------------------------------------------------------------------

interface SlotForm {
  readonly provider: AiProvider;
  /** A newly typed API key (empty ⇒ untouched, unless `keyCleared`). */
  readonly apiKey: string;
  /** True when the admin pressed "Clear key" (⇒ submit apiKey:''). */
  readonly keyCleared: boolean;
}

/** Seed a slot's form from its masked read DTO. Key field always starts empty. */
function formFromSlot(slot: AiProviderSlot): SlotForm {
  return { provider: slot.provider, apiKey: '', keyCleared: false };
}

/** The tri-state API-key intent the form submits for this slot. */
function apiKeyAction(form: SlotForm): ApiKeyAction {
  if (form.apiKey.length > 0) return { kind: 'set', value: form.apiKey };
  if (form.keyCleared) return { kind: 'clear' };
  return { kind: 'keep' };
}

function SourceBadge({
  source,
  testId,
}: {
  readonly source: AiProviderSlot['source'];
  readonly testId: string;
}): React.JSX.Element {
  const isDb = source === 'db';
  return (
    <span
      data-testid={testId}
      data-source={source}
      className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
        isDb ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {isDb ? 'saved' : 'from .env'}
    </span>
  );
}

interface SlotEditorProps {
  readonly title: string;
  /** Stable testid prefix: 'model-matrix-primary' | 'model-matrix-secondary'. */
  readonly prefix: string;
  readonly slot: AiProviderSlot;
  readonly form: SlotForm;
  readonly onChange: (next: SlotForm) => void;
}

function SlotEditor({ title, prefix, slot, form, onChange }: SlotEditorProps): React.JSX.Element {
  return (
    <section
      data-testid={`${prefix}-slot`}
      className="rounded-lg border border-border bg-white p-4"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-strong">{title}</h3>
        <SourceBadge source={slot.source} testId={`${prefix}-source`} />
      </div>

      <div className="grid gap-4">
        <label htmlFor={`${prefix}-provider`} className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">Provider</span>
          <select
            id={`${prefix}-provider`}
            data-testid={`${prefix}-provider`}
            className={FIELD_CLASS}
            value={form.provider}
            onChange={(e) => onChange({ ...form, provider: e.target.value as AiProvider })}
          >
            {AI_PROVIDERS.map((p) => (
              <option key={p} value={p}>
                {PROVIDER_LABEL[p]}
              </option>
            ))}
          </select>
        </label>

        <label htmlFor={`${prefix}-key`} className="block">
          <span className="mb-1 block text-xs font-medium text-text-strong">API key</span>
          <input
            id={`${prefix}-key`}
            data-testid={`${prefix}-key`}
            type="password"
            autoComplete="off"
            className={FIELD_CLASS}
            value={form.apiKey}
            placeholder={
              form.keyCleared
                ? 'key will be cleared (revert to .env)'
                : slot.hasKey && slot.maskedKey
                  ? `saved: ${slot.maskedKey}`
                  : 'no key set'
            }
            onChange={(e) => onChange({ ...form, apiKey: e.target.value, keyCleared: false })}
          />
          <div className="mt-1 flex items-center justify-between">
            <span data-testid={`${prefix}-key-note`} className="text-[11px] text-gray-500">
              {form.keyCleared
                ? 'key will be cleared on save'
                : slot.hasKey
                  ? 'key saved — leave empty to keep it'
                  : 'enter a key to set it'}
            </span>
            {slot.hasKey && !form.keyCleared ? (
              <button
                type="button"
                data-testid={`${prefix}-key-clear`}
                onClick={() => onChange({ ...form, apiKey: '', keyCleared: true })}
                className="text-[11px] text-red-500 hover:underline"
              >
                Clear key
              </button>
            ) : null}
            {form.keyCleared ? (
              <button
                type="button"
                data-testid={`${prefix}-key-clear-undo`}
                onClick={() => onChange({ ...form, keyCleared: false })}
                className="text-[11px] text-primary hover:underline"
              >
                Undo
              </button>
            ) : null}
          </div>
        </label>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Per-cell model picker — dropdown sourced from the given model list, plus a
// "Custom…" free-text escape hatch (no "default" option — every row always
// carries an explicit primary AND fallback model).
// ---------------------------------------------------------------------------

function ModelSelect({
  value,
  options,
  onChange,
  testId,
}: {
  readonly value: string;
  readonly options: readonly string[];
  readonly onChange: (next: string) => void;
  readonly testId: string;
}): React.JSX.Element {
  const inOptions = options.includes(value);
  // A non-empty value not in `options` (e.g. a previously-picked model the
  // provider's live "Get actual models" list no longer contains) stays
  // selected AS-IS, labeled stale — distinct from the Custom free-text
  // escape hatch, which only kicks in for a genuinely empty value.
  const isStale = value !== '' && !inOptions;
  const selectValue = inOptions || isStale ? value : CUSTOM_MODEL_OPTION;

  return (
    <div className="flex flex-col gap-1">
      <select
        data-testid={testId}
        className={`${FIELD_CLASS} font-mono text-xs`}
        value={selectValue}
        onChange={(e) => {
          const next = e.target.value;
          onChange(next === CUSTOM_MODEL_OPTION ? '' : next);
        }}
      >
        {isStale ? (
          <option data-testid={`${testId}-stale-option`} value={value}>
            {value} (stale)
          </option>
        ) : null}
        {options.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
        <option value={CUSTOM_MODEL_OPTION}>Custom…</option>
      </select>
      {selectValue === CUSTOM_MODEL_OPTION ? (
        <input
          data-testid={`${testId}-custom`}
          type="text"
          className={`${FIELD_CLASS} font-mono text-xs`}
          value={value}
          placeholder="e.g. claude-x-y"
          onChange={(e) => onChange(e.target.value)}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// View/Edit popup — the existing prompt editor fields, `model` REMOVED.
// ---------------------------------------------------------------------------

interface PromptForm {
  title: string;
  maxTokens: string;
  isActive: boolean;
  description: string;
  variables: string;
  promptTemplate: string;
  outputFormat: string;
}

function formFromPrompt(p: AdminPromptDTO): PromptForm {
  return {
    title: p.title || '',
    maxTokens: p.max_tokens != null ? String(p.max_tokens) : '',
    isActive: p.is_active !== false,
    description: p.description || '',
    variables: JSON.stringify(p.input_variables || []),
    promptTemplate: p.prompt_template || '',
    outputFormat: p.output_format || '',
  };
}

/**
 * Build the PATCH body from the editor form — the columns this popup edits
 * (title / description / prompt_template / max_tokens / is_active). Model
 * picks are NOT here: they are edited + saved from the matrix row instead
 * (input_variables / output_format stay client-side-only, matching the
 * legacy `Build Update SQL`, which never touched those columns).
 */
function bodyFromForm(form: PromptForm): AdminPromptUpdateBody {
  const parsedMax = Number.parseInt(form.maxTokens, 10);
  const maxTokens = Number.isFinite(parsedMax) && parsedMax > 0 ? parsedMax : null;
  return {
    title: form.title.trim(),
    description: form.description.trim(),
    max_tokens: maxTokens,
    is_active: form.isActive,
    prompt_template: form.promptTemplate,
  };
}

function labelledField(id: string, label: string, control: React.JSX.Element): React.JSX.Element {
  return (
    <label htmlFor={id} className="block">
      <span className="mb-1 block text-xs font-medium text-text-strong">{label}</span>
      {control}
    </label>
  );
}

interface PromptEditorDialogProps {
  readonly prompt: AdminPromptDTO;
  readonly onClose: () => void;
}

function PromptEditorDialog({ prompt, onClose }: PromptEditorDialogProps): React.JSX.Element {
  const toast = useToast();
  const updatePrompt = useUpdatePrompt();
  const [form, setForm] = useState<PromptForm>(() => formFromPrompt(prompt));

  function patch<K extends keyof PromptForm>(key: K, value: PromptForm[K]): void {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleSave(): void {
    // Legacy savePrompt: input_variables is JSON.parsed BEFORE save; an invalid
    // parse blocks the save with the "Invalid JSON in variables" error toast.
    const parsed = parseInputVariables(form.variables);
    if (!parsed.ok) {
      toast.error(INVALID_VARIABLES_JSON_MESSAGE);
      return;
    }

    updatePrompt.mutate(
      { promptKey: prompt.prompt_key, patch: bodyFromForm(form) },
      {
        onSuccess: () => {
          toast.success('Prompt saved');
          onClose();
        },
        onError: (error) => {
          toast.error(errorMessage(error, 'Save error'));
        },
      },
    );
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={prompt.title || prompt.prompt_key}
      maxWidth="2xl"
      testId="prompt-dialog"
      footer={
        <DialogFooter
          onCancel={onClose}
          onConfirm={handleSave}
          confirmLabel={updatePrompt.isPending ? 'Saving…' : 'Save'}
          confirmDisabled={updatePrompt.isPending}
          testId="prompt-dialog-footer"
        />
      }
    >
      <div className="grid gap-4">
        <div className="grid grid-cols-2 gap-4">
          {labelledField(
            'prompt-field-title',
            'Title',
            <input
              id="prompt-field-title"
              data-testid="prompt-field-title"
              type="text"
              className={FIELD_CLASS}
              value={form.title}
              onChange={(e) => patch('title', e.target.value)}
            />,
          )}
          {labelledField(
            'prompt-field-max-tokens',
            'Max tokens',
            <input
              id="prompt-field-max-tokens"
              data-testid="prompt-field-max-tokens"
              type="number"
              className={FIELD_CLASS}
              value={form.maxTokens}
              onChange={(e) => patch('maxTokens', e.target.value)}
            />,
          )}
        </div>

        <label className="flex items-center gap-2 text-sm text-text-strong">
          <input
            data-testid="prompt-field-active"
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => patch('isActive', e.target.checked)}
          />
          Active
        </label>

        {labelledField(
          'prompt-field-description',
          'Description',
          <input
            id="prompt-field-description"
            data-testid="prompt-field-description"
            type="text"
            className={FIELD_CLASS}
            value={form.description}
            onChange={(e) => patch('description', e.target.value)}
          />,
        )}

        {labelledField(
          'prompt-field-vars',
          'Input variables (JSON)',
          <input
            id="prompt-field-vars"
            data-testid="prompt-field-vars"
            type="text"
            className={`${FIELD_CLASS} font-mono`}
            value={form.variables}
            onChange={(e) => patch('variables', e.target.value)}
          />,
        )}

        {labelledField(
          'prompt-field-template',
          'Prompt template',
          <textarea
            id="prompt-field-template"
            data-testid="prompt-field-template"
            rows={8}
            className={`${FIELD_CLASS} font-mono`}
            value={form.promptTemplate}
            onChange={(e) => patch('promptTemplate', e.target.value)}
          />,
        )}

        {labelledField(
          'prompt-field-output',
          'Output format',
          <input
            id="prompt-field-output"
            data-testid="prompt-field-output"
            type="text"
            className={FIELD_CLASS}
            value={form.outputFormat}
            onChange={(e) => patch('outputFormat', e.target.value)}
          />,
        )}

        <p className="text-[11px] text-gray-400">
          Note: the Model field is intentionally not here any more — Primary/Fallback model now live
          in this prompt&apos;s row in the matrix table.
        </p>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Matrix table
// ---------------------------------------------------------------------------

interface PendingModels {
  readonly primaryModel: string;
  readonly fallbackModel: string;
}

function ModelMatrixTable({
  prompts,
  primaryOptions,
  fallbackOptions,
  onOpenEditor,
  onOpenTest,
}: {
  readonly prompts: readonly AdminPromptDTO[];
  readonly primaryOptions: readonly string[];
  readonly fallbackOptions: readonly string[];
  readonly onOpenEditor: (prompt: AdminPromptDTO) => void;
  readonly onOpenTest: (prompt: AdminPromptDTO) => void;
}): React.JSX.Element {
  const toast = useToast();
  const updatePrompt = useUpdatePrompt();
  const [pending, setPending] = useState<Map<string, PendingModels>>(new Map());
  const [isSaving, setIsSaving] = useState(false);

  const baseline = useMemo(
    () =>
      new Map(
        prompts.map((p) => [
          p.prompt_key,
          { primaryModel: p.primary_model, fallbackModel: p.fallback_model },
        ]),
      ),
    [prompts],
  );

  // A saved server round-trip refetches `prompts`, changing `baseline` — drop
  // any pending edit that now matches the fresh baseline (successfully saved).
  useEffect(() => {
    setPending((prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const [key, value] of prev) {
        const base = baseline.get(key);
        if (
          base &&
          base.primaryModel === value.primaryModel &&
          base.fallbackModel === value.fallbackModel
        ) {
          next.delete(key);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [baseline]);

  const grouped = useMemo(() => {
    const map = new Map<GroupId, AdminPromptDTO[]>();
    for (const group of GROUP_ORDER) map.set(group, []);
    for (const p of prompts) {
      map.get(groupForPromptKey(p.prompt_key))!.push(p);
    }
    return map;
  }, [prompts]);

  function currentValues(promptKey: string): PendingModels {
    return pending.get(promptKey) ?? baseline.get(promptKey)!;
  }

  function setCell(promptKey: string, patch: Partial<PendingModels>): void {
    setPending((prev) => {
      const next = new Map(prev);
      const merged = { ...currentValues(promptKey), ...patch };
      const base = baseline.get(promptKey)!;
      if (
        merged.primaryModel === base.primaryModel &&
        merged.fallbackModel === base.fallbackModel
      ) {
        next.delete(promptKey);
      } else {
        next.set(promptKey, merged);
      }
      return next;
    });
  }

  const dirtyCount = pending.size;

  async function handleSave(): Promise<void> {
    if (dirtyCount === 0) return;
    const entries = Array.from(pending.entries());
    setIsSaving(true);
    try {
      await Promise.all(
        entries.map(([promptKey, values]) =>
          updatePrompt.mutateAsync({
            promptKey,
            patch: { primary_model: values.primaryModel, fallback_model: values.fallbackModel },
          }),
        ),
      );
      toast.success(`Model picks saved for ${entries.length} task(s)`);
    } catch (error) {
      toast.error(errorMessage(error, 'Failed to save model picks'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <section data-testid="model-matrix-table" className="rounded-lg border border-border bg-white">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[760px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-border bg-surface-muted text-left text-xs font-medium text-text-muted">
              <th className="px-4 py-3">Task</th>
              <th className="px-4 py-3">Primary model</th>
              <th className="px-4 py-3">Fallback model</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {GROUP_ORDER.flatMap((group) => {
              const rows = grouped.get(group) ?? [];
              if (rows.length === 0) return [];
              return [
                <tr key={`group-${group}`} data-testid={`model-matrix-group-${groupSlug(group)}`}>
                  <td colSpan={4} className="border-b border-border bg-surface-muted px-4 py-2">
                    <span className="brand-font text-xs font-semibold uppercase tracking-wide text-text-strong">
                      {GROUP_LABEL[group]}
                    </span>
                    <span className="ml-2 text-[11px] text-text-muted">
                      {rows.length} task{rows.length === 1 ? '' : 's'}
                    </span>
                  </td>
                </tr>,
                ...rows.map((p) => {
                  const values = currentValues(p.prompt_key);
                  const isDirty = pending.has(p.prompt_key);
                  return (
                    <tr
                      key={p.prompt_key}
                      data-testid="model-matrix-row"
                      data-prompt-key={p.prompt_key}
                      className={`border-b border-border last:border-b-0 ${isDirty ? 'bg-accent/5' : ''}`}
                    >
                      <td className="max-w-[280px] px-4 py-3 align-top">
                        <div className="flex flex-wrap items-center gap-2">
                          <span
                            data-testid="model-matrix-title"
                            className="text-sm font-medium text-text-strong"
                          >
                            {p.title}
                          </span>
                          {p.is_active === false ? (
                            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] text-red-600">
                              Inactive
                            </span>
                          ) : null}
                        </div>
                        <p className="mt-0.5 font-mono text-[11px] text-text-muted">
                          {p.prompt_key}
                        </p>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <ModelSelect
                          testId="model-matrix-primary-select"
                          value={values.primaryModel}
                          options={primaryOptions}
                          onChange={(next) => setCell(p.prompt_key, { primaryModel: next })}
                        />
                      </td>
                      <td className="px-4 py-3 align-top">
                        <ModelSelect
                          testId="model-matrix-fallback-select"
                          value={values.fallbackModel}
                          options={fallbackOptions}
                          onChange={(next) => setCell(p.prompt_key, { fallbackModel: next })}
                        />
                      </td>
                      <td className="px-4 py-3 text-right align-top">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            type="button"
                            data-testid="model-matrix-edit"
                            onClick={() => onOpenEditor(p)}
                            className="rounded border border-border px-3 py-1.5 text-xs font-medium text-text-strong hover:bg-gray-50"
                          >
                            View/Edit
                          </button>
                          {/* E05-F09 — replay this prompt on a recorded run's
                              inputs. Separate from View/Edit on purpose: that
                              popup saves, this one runs. */}
                          <button
                            type="button"
                            data-testid="model-matrix-test"
                            title="Test this prompt on the inputs of a recorded run"
                            onClick={() => onOpenTest(p)}
                            className="rounded border border-primary px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary hover:text-white"
                          >
                            Test
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                }),
              ];
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between border-t border-border px-4 py-3">
        <span className="text-[11px] text-text-muted">
          {dirtyCount > 0 ? `${dirtyCount} row(s) changed, not saved yet` : 'No unsaved changes'}
        </span>
        <button
          type="button"
          data-testid="model-matrix-save"
          onClick={() => void handleSave()}
          disabled={dirtyCount === 0 || isSaving}
          className="rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {isSaving ? 'Saving…' : dirtyCount > 0 ? `Save (${dirtyCount} changed)` : 'Save'}
        </button>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Screen root
// ---------------------------------------------------------------------------

/**
 * The Model Matrix admin tab. Reads the masked provider settings + the full
 * prompt list, renders the two provider cards + the grouped matrix table, and
 * opens the editor popup on "View/Edit". Two independent Save actions (see
 * module docblock).
 */
export function ModelMatrixTab(): React.JSX.Element {
  const providersQuery = useAiProviders();
  const updateProviders = useUpdateAiProviders();
  const promptsQuery = usePrompts();
  const modelsQuery = useAiProviderModels();
  const refreshModels = useRefreshAiProviderModels();
  const toast = useToast();

  const [primaryForm, setPrimaryForm] = useState<SlotForm | null>(null);
  const [secondaryForm, setSecondaryForm] = useState<SlotForm | null>(null);
  const [editing, setEditing] = useState<AdminPromptDTO | null>(null);
  /** E05-F09 — the row whose "Test prompt" window is open. */
  const [testing, setTesting] = useState<AdminPromptDTO | null>(null);

  const providerData = providersQuery.data;
  useEffect(() => {
    if (providerData) {
      setPrimaryForm(formFromSlot(providerData.primary));
      setSecondaryForm(formFromSlot(providerData.secondary));
    }
  }, [providerData]);

  const prompts = useMemo(() => promptsQuery.data ?? [], [promptsQuery.data]);

  // Live-fetched model lists (successful providers only) — passive on mount,
  // refreshed on demand by the "Get actual models" button. A provider absent
  // here (never fetched, or its only fetch failed) falls back to the static
  // AI_PROVIDER_MODELS list below.
  const fetchedModelsByProvider = useMemo(() => {
    const map: Partial<Record<AiProvider, readonly string[]>> = {};
    for (const r of modelsQuery.data ?? []) {
      if (r.ok) map[r.provider] = r.models;
    }
    return map;
  }, [modelsQuery.data]);

  if (providersQuery.isLoading || promptsQuery.isLoading) {
    return (
      <div data-testid="model-matrix-loading" className="py-4 text-center text-gray-400">
        Loading…
      </div>
    );
  }

  if (providersQuery.isError || !providerData) {
    return (
      <div data-testid="model-matrix-error" className="py-4 text-center text-red-500">
        {errorMessage(providersQuery.error, 'Failed to load AI provider settings')}
      </div>
    );
  }

  if (promptsQuery.isError) {
    return (
      <div data-testid="model-matrix-error" className="py-4 text-center text-red-500">
        {errorMessage(promptsQuery.error, 'Failed to load prompts')}
      </div>
    );
  }

  // Forms are seeded by the effect on the first render after data arrives.
  if (!primaryForm || !secondaryForm) {
    return (
      <div data-testid="model-matrix-loading" className="py-4 text-center text-gray-400">
        Loading…
      </div>
    );
  }

  function handleSaveProviders(): void {
    if (!primaryForm || !secondaryForm) return;
    updateProviders.mutate(
      {
        primary: slotUpdateFromForm({
          provider: primaryForm.provider,
          apiKeyAction: apiKeyAction(primaryForm),
        }),
        secondary: slotUpdateFromForm({
          provider: secondaryForm.provider,
          apiKeyAction: apiKeyAction(secondaryForm),
        }),
      },
      {
        onSuccess: () => {
          toast.success('AI provider settings saved');
        },
        onError: (error) => {
          toast.error(errorMessage(error, 'Failed to save settings'));
        },
      },
    );
  }

  const primaryOptions =
    fetchedModelsByProvider[providerData.primary.provider] ??
    AI_PROVIDER_MODELS[providerData.primary.provider];
  const fallbackOptions =
    fetchedModelsByProvider[providerData.secondary.provider] ??
    AI_PROVIDER_MODELS[providerData.secondary.provider];

  function handleRefreshModels(): void {
    refreshModels.mutate(undefined, {
      onSuccess: (results) => {
        const failed = results.filter((r) => !r.ok);
        for (const r of failed) {
          toast.error(`${PROVIDER_LABEL[r.provider]}: ${r.error}`);
        }
        const succeeded = results.filter((r) => r.ok).length;
        if (succeeded > 0) {
          toast.success(`Model list refreshed for ${succeeded} provider(s)`);
        } else if (failed.length === 0) {
          toast.error('No configured provider has an API key to fetch models with');
        }
      },
      onError: (error) => {
        toast.error(errorMessage(error, 'Failed to fetch models'));
      },
    });
  }

  return (
    <div data-testid="model-matrix-tab" className="grid gap-6">
      <p className="text-xs text-gray-500">
        Choose the active AI provider + API key per slot, then set each task&apos;s primary and
        fallback model in the table below. Keys are stored encrypted; an empty key reverts to the
        value from .env.
      </p>

      <div className="grid gap-6 md:grid-cols-2">
        <SlotEditor
          title="Primary"
          prefix="model-matrix-primary"
          slot={providerData.primary}
          form={primaryForm}
          onChange={setPrimaryForm}
        />
        <SlotEditor
          title="Secondary"
          prefix="model-matrix-secondary"
          slot={providerData.secondary}
          form={secondaryForm}
          onChange={setSecondaryForm}
        />
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          data-testid="model-matrix-providers-save"
          onClick={handleSaveProviders}
          disabled={updateProviders.isPending}
          className="rounded bg-primary px-4 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
        >
          {updateProviders.isPending ? 'Saving…' : 'Save providers'}
        </button>
      </div>

      <div className="flex items-center justify-between">
        <span className="text-[11px] text-text-muted">
          Model dropdowns below show each configured provider&apos;s live list once fetched via
          &quot;Get actual models&quot;; until then, a provider&apos;s currently saved model still
          shows as its own option.
        </span>
        <button
          type="button"
          data-testid="model-matrix-refresh-models"
          onClick={handleRefreshModels}
          disabled={refreshModels.isPending}
          className="rounded border border-border px-4 py-2 text-sm font-medium text-text-strong hover:bg-gray-50 disabled:opacity-50"
        >
          {refreshModels.isPending ? 'Fetching…' : 'Get actual models'}
        </button>
      </div>

      <ModelMatrixTable
        prompts={prompts}
        primaryOptions={primaryOptions}
        fallbackOptions={fallbackOptions}
        onOpenEditor={setEditing}
        onOpenTest={setTesting}
      />

      {editing ? <PromptEditorDialog prompt={editing} onClose={() => setEditing(null)} /> : null}
      {testing ? <PromptTestDialog prompt={testing} onClose={() => setTesting(null)} /> : null}
    </div>
  );
}

export default ModelMatrixTab;
